import type { IssueTypeIdentifier, IssueTypePriority, IssueTypeStatus, Stored, User } from "@overmap-ai/common"
import { FieldSchema } from "@overmap-ai/forms"

import type { IssueData } from "../../typings"
import { AssigneeFilterDef } from "../defs/AssigneeFilterDef"
import { CreatedByFilterDef } from "../defs/CreatedByFilterDef"
import { DueDateFilterDef } from "../defs/DueDateFilterDef"
import { IssuePriorityFilterDef } from "../defs/IssuePriorityFilterDef"
import { IssueTypeStatusFilterDef } from "../defs/IssueStatusFilterDef"
import { IssueTitleFilterDef } from "../defs/IssueTitleFilterDef"
import { SubmittedAtFilterDef } from "../defs/SubmittedAtFilterDef"
import { getFilterDefConfigsForFieldSchema, getFilterDefsOfIdentifiers } from "../defs/utils"
import { FilterSchema } from "./FilterSchema"
import { createFilterDefConfig } from "./utils"

interface IssueDataFilterSchemaConfig {
	assignees: User[]
	issueTypeIdentifiers: Stored<IssueTypeIdentifier>[]
	issueTypePriorities: Stored<IssueTypePriority>[]
	issueTypeStatuses: Stored<IssueTypeStatus>[]
	issueTypeFieldSchema: FieldSchema
	users: User[]
}

export const getIssueDataFilterSchema = (config: IssueDataFilterSchemaConfig) => {
	const { assignees, issueTypeIdentifiers, issueTypePriorities, issueTypeStatuses, issueTypeFieldSchema, users } =
		config

	return FilterSchema.create<IssueData>({
		filterDefs: [
			createFilterDefConfig({
				filterDef: new IssueTitleFilterDef(),
				accessorFn: ({ issue }) => issue.title ?? null,
			}),
			createFilterDefConfig({
				filterDef: new CreatedByFilterDef(users),
				accessorFn: ({ issue }) => issue.created_by ?? -1,
			}),
			createFilterDefConfig({
				filterDef: new SubmittedAtFilterDef(),
				accessorFn: ({ issue }) => new Date(issue.submitted_at),
			}),
			createFilterDefConfig({
				filterDef: new IssueTypeStatusFilterDef(issueTypeStatuses),
				accessorFn: ({ issue }) => issue.status,
			}),
			createFilterDefConfig({
				filterDef: new IssuePriorityFilterDef(issueTypePriorities),
				accessorFn: ({ issue }) => issue.priority,
			}),
			createFilterDefConfig({
				filterDef: new AssigneeFilterDef(assignees),
				accessorFn: ({ issue }) => issue.assigned_to ?? null,
			}),
			createFilterDefConfig({
				filterDef: new DueDateFilterDef(),
				accessorFn: ({ issue }) => (issue.due_date ? new Date(issue.due_date) : null),
			}),
			...getFilterDefsOfIdentifiers<Stored<IssueTypeIdentifier>, IssueData>(
				issueTypeIdentifiers,
				(identifier) => (data) => data.issueTypeIdentifierValues[identifier.uuid]?.value ?? null,
			),
			...getFilterDefConfigsForFieldSchema<IssueData>(issueTypeFieldSchema, (data) => [
				issueTypeFieldSchema.deserializeValues(data.issueTypeFieldValues?.values ?? {}),
				data.issueTypeFieldValuesAttachments,
			]),
		],
	})
}
